import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Button, Spinner } from "react-bootstrap";
import { getAllBerita, BASE_URL } from "../services/api";
import { useLanguage, pickLang } from "../contexts/LanguageContext";
import { findBeritaBySlugOrId } from "../utils/beritaSlug";
import "./BeritaDetail.css";

const BeritaDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [berita, setBerita] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchBeritaDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchBeritaDetail = async () => {
    try {
      setLoading(true);
      setError(false);
      const response = await getAllBerita();
      if (response.data.success) {
        const foundBerita = findBeritaBySlugOrId(response.data.data, slug);
        if (foundBerita) {
          setBerita(foundBerita);
        } else {
          setError(true);
        }
      }
    } catch (error) {
      console.error("Error fetching berita:", error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString("id-ID", options);
  };

  if (loading) {
    return (
      <div className="berita-detail-loading">
        <Container>
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">
              {language === "en" ? "Loading news..." : "Memuat berita..."}
            </p>
          </div>
        </Container>
      </div>
    );
  }

  if (error || !berita) {
    return (
      <div className="berita-detail-error">
        <Container>
          <div className="text-center py-5">
            <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
            <h3>
              {language === "en" ? "News Not Found" : "Berita Tidak Ditemukan"}
            </h3>
            <p className="text-muted mb-4">
              {language === "en"
                ? "Sorry, the news you are looking for is not available."
                : "Maaf, berita yang Anda cari tidak tersedia."}
            </p>
            <Button variant="primary" onClick={() => navigate("/")}>
              <i className="fas fa-home me-2"></i>
              {language === "en" ? "Back to Home" : "Kembali ke Beranda"}
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="berita-detail-page">
      <Container>
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            {/* Breadcrumb */}
            <nav aria-label="breadcrumb" className="berita-breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Button
                    variant="link"
                    className="p-0"
                    onClick={() => navigate("/")}
                  >
                    <i className="fas fa-home me-1"></i>
                    Beranda
                  </Button>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Berita
                </li>
              </ol>
            </nav>

            {/* Article Header */}
            <article className="berita-article">
              <header className="article-header">
                <h1 className="article-title">
                  {pickLang(berita, "judul", language)}
                </h1>

                <div className="article-meta">
                  <div className="meta-item">
                    <i className="fas fa-calendar-alt"></i>
                    <span>{formatDate(berita.tanggal)}</span>
                  </div>
                  <div className="meta-item">
                    <i className="fas fa-user"></i>
                    <span>{berita.pengirim || "Admin"}</span>
                  </div>
                  {berita.hits && (
                    <div className="meta-item">
                      <i className="fas fa-eye"></i>
                      <span>{berita.hits} views</span>
                    </div>
                  )}
                </div>
              </header>

              {/* Featured Image */}
              {berita.foto && (
                <div className="article-featured-image">
                  <img
                    src={`${BASE_URL}/uploads/${berita.foto}`}
                    alt={pickLang(berita, "judul", language)}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}

              {/* Article Content */}
              <div
                className="article-content"
                dangerouslySetInnerHTML={{
                  __html: pickLang(berita, "isiberita", language),
                }}
              />

              {/* Article Footer */}
              <footer className="article-footer">
                <div className="d-flex justify-content-between align-items-center">
                  <Button
                    variant="outline-primary"
                    onClick={() => navigate("/")}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    {language === "en" ? "Back to Home" : "Kembali ke Beranda"}
                  </Button>

                  <div className="share-buttons">
                    <span className="me-2">
                      {language === "en" ? "Share:" : "Bagikan:"}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-2"
                      onClick={() => {
                        const url = window.location.href;
                        navigator.clipboard.writeText(url);
                        alert("Link berhasil disalin!");
                      }}
                    >
                      <i className="fas fa-link"></i>
                    </Button>
                  </div>
                </div>
              </footer>
            </article>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BeritaDetail;
